/*
 * Copyright (c) 2024 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, July 11th 2024, 3:54:40 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import * as uuid from "uuid";
import { FormField, FIELD_TYPES, TGeoFieldLocationDataTargetConfig, GeoLocationDataTypes } from "lib/schemas";
import { useOSFormContext } from "ui-widgets/forms/providers";
import { useListEditorContext } from "lib/providers";
import * as commonUtils from "lib/utils/common-utils";
import {
  ValueSelector,
  OSActionButton,
  CheckBoxSelector,
  OSAlert,
  OSDeleteButton,
  EmptyStateActionWrapper,
  Image,
  EmphasisText,
} from "ui-widgets";

export const GeoFieldLocationDataTargetConfig = () => {
  const {
    itemsListState: { savedItems: allFields },
  } = useListEditorContext<FormField>();
  const availableFieldsToAdd = allFields.filter((field) => field.field_type !== FIELD_TYPES.GEO_COORDINATES);
  const availableFieldSlugs = availableFieldsToAdd.map((field) => field.slug);

  const { watch, setValue, reset } = useOSFormContext<FormField>();
  const locationDataTargetConfig: TGeoFieldLocationDataTargetConfig =
    watch("extra_configurations.geo_field_settings.geo_location_data_target_fields") || {};
  const locationDataTargetFieldsConfig = locationDataTargetConfig.fields_to_location_data_mapping || {};
  const locationDataTargetFieldSlugs = Object.keys(locationDataTargetFieldsConfig);

  const unconfiguredFields = allFields.filter((field) => {
    return !locationDataTargetFieldSlugs.includes(field.slug) && field.field_type !== FIELD_TYPES.GEO_COORDINATES;
  });

  const isGeoDataTargetConfigValid = (config: TGeoFieldLocationDataTargetConfig) => {
    const slugs = Object.keys(config.fields_to_location_data_mapping || {});
    return (
      slugs.length > 0 &&
      slugs.every(
        (fieldSlug) =>
          availableFieldSlugs.includes(fieldSlug) && config.fields_to_location_data_mapping[fieldSlug].length > 0
      )
    );
  };

  const onChangeConfig = (config: TGeoFieldLocationDataTargetConfig) => {
    reset(watch());
    setValue("extra_configurations.geo_field_settings.geo_location_data_target_fields", config, {
      shouldDirty: isGeoDataTargetConfigValid(config),
    });
  };

  const onChangeTargetFields = (
    fieldSlug: string,
    locationData: TGeoFieldLocationDataTargetConfig["fields_to_location_data_mapping"][string]
  ) => {
    onChangeConfig({
      ...locationDataTargetConfig,
      fields_to_location_data_mapping: {
        ...locationDataTargetFieldsConfig,
        [fieldSlug]: locationData,
      },
    });
  };

  const onRemoveField = (fieldSlug: string) => {
    const newFieldsObject = { ...locationDataTargetFieldsConfig };
    delete newFieldsObject[fieldSlug];
    onChangeConfig({
      ...locationDataTargetConfig,
      fields_to_location_data_mapping: newFieldsObject,
    });
  };

  const onAddNewField = () => {
    onChangeTargetFields(uuid.v4(), []);
  };

  const onChangeField = (oldFieldSlug: string, newFieldSlug: string) => {
    const locationData = locationDataTargetFieldsConfig[oldFieldSlug] || [];
    const newFieldsObject = { ...locationDataTargetFieldsConfig, [newFieldSlug]: locationData };
    delete newFieldsObject[oldFieldSlug];
    onChangeConfig({
      ...locationDataTargetConfig,
      fields_to_location_data_mapping: newFieldsObject,
    });
  };

  const onCheckLocationDataItem = (
    fieldSlug: string,
    locationDataItem: TGeoFieldLocationDataTargetConfig["fields_to_location_data_mapping"][string][0]
  ) => {
    let newLocationData = [...locationDataTargetFieldsConfig[fieldSlug]];
    if (newLocationData.includes(locationDataItem)) {
      newLocationData = newLocationData.filter((item) => item !== locationDataItem);
    } else {
      newLocationData = [...locationDataTargetFieldsConfig[fieldSlug], locationDataItem];
    }

    onChangeTargetFields(fieldSlug, Array.from(new Set(newLocationData)));
  };

  if (availableFieldsToAdd.length === 0)
    return (
      <GeoSettingsEmpty
        actions={<EmphasisText>To add location data targets, add other fields to this form first.</EmphasisText>}
      />
    );

  return (
    <>
      {locationDataTargetFieldSlugs?.length > 0 && (
        <Stack spacing={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Target Field</TableCell>
                <TableCell>Location Data</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {locationDataTargetFieldSlugs.map((fieldSlug) => {
                const locationDataForField = locationDataTargetFieldsConfig[fieldSlug];
                return (
                  <TableRow key={fieldSlug} sx={{ pt: 2 }}>
                    <TableCell>
                      <ValueSelector
                        choices={availableFieldsToAdd
                          .map((field) => ({ label: field.label, value: field.slug }))
                          .filter(({ value }) => {
                            if (value === fieldSlug) return true;
                            return !locationDataTargetFieldSlugs.includes(value);
                          })}
                        chosenValue={fieldSlug}
                        onChangeValue={(slug) => onChangeField(fieldSlug, slug as string)}
                      />
                    </TableCell>

                    <TableCell>
                      <CheckBoxSelector
                        values={locationDataForField}
                        onClickItem={(item) => {
                          onCheckLocationDataItem(fieldSlug, item);
                        }}
                        options={
                          Object.keys(
                            GeoLocationDataTypes
                          ) as TGeoFieldLocationDataTargetConfig["fields_to_location_data_mapping"][string]
                        }
                        getOptionLabel={(option) => {
                          const index = locationDataForField.indexOf(option);
                          const position = index + 1;
                          return (
                            <>
                              {GeoLocationDataTypes[option]}{" "}
                              {position >= 1 ? (
                                <Tooltip
                                  title={
                                    <Typography>
                                      When the target field is updated, {GeoLocationDataTypes[option]} be the{" "}
                                      {commonUtils.ordinal(position)} item in the combined location data.
                                    </Typography>
                                  }
                                >
                                  <span>
                                    <EmphasisText>({position})</EmphasisText>
                                  </span>
                                </Tooltip>
                              ) : null}
                            </>
                          );
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <OSDeleteButton
                        toolTipText={<Typography>Delete Target</Typography>}
                        onClick={() => onRemoveField(fieldSlug)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}

              <TableRow>
                <TableCell colSpan={4}>
                  {unconfiguredFields?.length > 0 ? (
                    <Tooltip title={<Typography>Add Location Data Target Field</Typography>} placement="top" arrow>
                      <div>
                        <OSActionButton onClick={onAddNewField} buttonType="floating" actionStyle="add" />
                      </div>
                    </Tooltip>
                  ) : (
                    <OSAlert>
                      All Fields in this form have been assigned targets, to modify the configuration, edit the current
                      set of fields
                    </OSAlert>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Stack>
      )}
      {locationDataTargetFieldSlugs?.length === 0 && (
        <GeoSettingsEmpty
          actions={
            <OSActionButton variant="contained" size="large" onClick={onAddNewField}>
              Add Location Data Target Field
            </OSActionButton>
          }
        />
      )}
    </>
  );
};

const GeoSettingsEmpty = ({ actions }) => (
  <EmptyStateActionWrapper
    title="No Location Data Targets."
    description={
      <>
        Specify other fields on this form that should automatically be updated with location data when the value of this
        geo field changes.
      </>
    }
    illustrationGraphic={<Image className="image-style-A" src="/img/location.png" alt="Onestop Admin" />}
  >
    {actions}
  </EmptyStateActionWrapper>
);
